import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EstateSummary, listEstates, statusBadge } from "./api";

/** Portfolio view — fleet overview across all monitored estates. */
export function Portfolio() {
  const [estates, setEstates] = useState<EstateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    listEstates()
      .then((d) => setEstates(d))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
    const id = setInterval(() => {
      listEstates().then(setEstates).catch(() => {});
    }, 15_000);
    return () => clearInterval(id);
  }, []);

  // ----- KPI summaries -----
  const k = useMemo(() => {
    const totalEstates = estates.length;
    const allServices = estates.flatMap((e) => e.services);
    const totalServices = allServices.length;
    const operational = allServices.filter((s) => s.latest_status === "operational").length;
    const offline = allServices.filter((s) => s.latest_status === "offline").length;
    const degraded = allServices.filter((s) => s.latest_status === "degraded").length;
    const avgLatency =
      allServices.filter((s) => typeof s.latest_latency_ms === "number")
        .reduce((acc, s) => acc + (s.latest_latency_ms || 0), 0) /
      Math.max(1, allServices.filter((s) => typeof s.latest_latency_ms === "number").length);
    return { totalEstates, totalServices, operational, offline, degraded, avgLatency };
  }, [estates]);

  return (
    <>
      <h1>Portfolio</h1>
      <p className="subtitle">
        Fleet-wide status across {k.totalEstates} estate{k.totalEstates === 1 ? "" : "s"}.
      </p>

      <div className="kpis">
        <Kpi label="Estates"             value={k.totalEstates} />
        <Kpi label="Services monitored"  value={k.totalServices} />
        <Kpi
          label="Operational"
          value={`${k.operational}/${k.totalServices || 0}`}
          sub={k.offline + k.degraded > 0 ? `${k.offline} offline · ${k.degraded} degraded` : "all green"}
          tone={k.offline > 0 ? "down" : k.degraded > 0 ? "warn" : "ok"}
        />
        <Kpi
          label="Avg latency"
          value={k.totalServices ? `${k.avgLatency.toFixed(1)} ms` : "—"}
        />
      </div>

      <h2>Estates</h2>

      {loading && <div className="empty"><span className="spinner" /> Loading estates…</div>}
      {err && <div className="empty" style={{ color: "var(--down)" }}>Couldn't reach the API — {err}</div>}
      {!loading && !err && estates.length === 0 && (
        <div className="empty">No estates yet. Seed one with <code>scripts/seed-&lt;name&gt;.ts</code>.</div>
      )}

      <div className="estate-grid">
        {estates.map((e) => {
          const services = e.services || [];
          const operational = services.filter((s) => s.latest_status === "operational").length;
          const worst =
            services.find((s) => s.latest_status === "offline") ||
            services.find((s) => s.latest_status === "degraded");
          const overallBadge = worst ? statusBadge(worst.latest_status) : "ok";
          return (
            <Link key={e.id} to={`/estates/${e.id}`} className="estate-card">
              <div className="row">
                <h3>{e.profile?.name || e.id}</h3>
                <span className={`dot ${overallBadge}`} />
              </div>
              <div className="meta">{e.profile?.address || ""}</div>
              <div className="status-line">
                <span><strong>{operational}/{services.length}</strong> operational</span>
                {worst && (
                  <span style={{ color: "var(--down)" }}>
                    {worst.name || worst.service_id}: {worst.latest_status}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: { label: string; value: React.ReactNode; sub?: string; tone?: "ok" | "warn" | "down" }) {
  const colour =
    tone === "down" ? "var(--down)" : tone === "warn" ? "var(--warn)" : undefined;
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={colour ? { color: colour } : undefined}>{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}
